import type { Nil, PartialWithNil } from "@mcwd/typescript-type-guards";
import { deleteEmptyProperties } from "@mcwd/util-parse-and-convert";
import type { SiteCountryCodeUppercase, LocaleUrlPrefix } from "../../../types-and-definitions/locale-defs/Locales.js";
import type { FormWidgetData } from "../../../widgetDataTs.js";
import type { GatingLevel } from "../../types/definitions/gating/gating-level.js";
import type { GatingState } from "../../types/definitions/gating/gating-state.js";
import type { HiddenMarketoInputType, SimpleForm_HiddenMarketoInputName } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
import { getHiddenFieldValuesForSimpleForm } from "./getHiddenFieldValuesForSimpleForm.js";
import { getRoiCalcUrlQueryString } from "./getRoiCalcUrlQueryString.js";
import { doesUsePageOfferId, getLastOfferName, getResourceUrlForEmail } from "./resource-values/index.js";
import type { AnyFormSettingsKey } from "../../form-settings/form-experience/form-settings-keys.js";


export function getHiddenFieldValues (opts: {
  formTrackingName: string;
  leadSource: string | Nil;
  siteCountryCode: SiteCountryCodeUppercase;
  widgetData: FormWidgetData;
  formSettingsKey: AnyFormSettingsKey;
  initialFormGatingLevel: GatingLevel.MarketoForm;
  initialFormExperienceGatingLevel: GatingLevel.FormExperience;
  formGatingState: GatingState.MarketoForm;
  experienceGatingState: GatingState.FormExperience;
  customDocumentDownloadURL?: string | undefined;
  localeUrlPrefix: LocaleUrlPrefix;
}): PartialWithNil<HiddenMarketoInputType>
{
  const {
    formSettingsKey, widgetData, initialFormGatingLevel, initialFormExperienceGatingLevel, formGatingState, experienceGatingState, customDocumentDownloadURL, localeUrlPrefix
  } = opts;

  const simpleFormHiddenVals = getHiddenFieldValuesForSimpleForm(opts);
  const widgetSetup = widgetData.setup;
  const remainingHiddenVals = {
    Last_Offer_Name__c: getLastOfferName(formSettingsKey, widgetSetup),
    initialFormGatingLevel: initialFormGatingLevel,
    initialFormExperienceGatingLevel: initialFormExperienceGatingLevel,
    experienceGatingState: experienceGatingState,
    formGatingState: formGatingState,
    documentDownloadURL: getResourceUrlForEmail({ customDocumentDownloadURL, formSettingsKey, localeUrlPrefix, widgetSetup }),
    roi_querystring__c_contact: getRoiCalcUrlQueryString(formSettingsKey),
    multiDocSelectorFormFill: doesUsePageOfferId(widgetSetup),
  } as const satisfies Required<PartialWithNil<Omit<HiddenMarketoInputType, SimpleForm_HiddenMarketoInputName>>>;
  const val: PartialWithNil<HiddenMarketoInputType> = {
    ...simpleFormHiddenVals,
    ...deleteEmptyProperties(remainingHiddenVals)
  } as const satisfies PartialWithNil<HiddenMarketoInputType>;

  return val;
}
